import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useManualLogin } from "../../hooks/useAuth";
import {
  Box,
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  IconButton,
  InputAdornment,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";

import PMC from "../../assets/images/layout/Seal_of_Maharashtra.png";
import PMCLogo from "../../assets/images/layout/Emblem_of_India.png";

const LoginPage = () => {
  const [formData, setFormData] = useState({
    username: "",
    password: "",
  });
  const [showPassword, setShowPassword] = useState(false);

  const { handleLogin, isLoading, error } = useManualLogin();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.between("sm", "md"));

  const handleSubmit = async (e) => {
    e.preventDefault();

    const result = await handleLogin(formData.username, formData.password);

    if (result.success) {
      navigate("/");
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleTogglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        height: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: { xs: 1, sm: 2, md: 3 },
        backgroundImage: `url(${PMC})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
        position: "relative",
        overflow: "auto",
        "&::before": {
          content: '""',
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: "rgba(6, 28, 54, 0.75)",
          zIndex: 1,
        },
      }}
    >
      <Container
        component="main"
        maxWidth="sm"
        sx={{
          position: "relative",
          zIndex: 2,
          width: "100%",
          padding: { xs: "16px", sm: "24px" },
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            width: "100%",
          }}
        >
          <Paper
            elevation={8}
            sx={{
              padding: {
                xs: 3,
                sm: 4,
                md: 5,
              },
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              width: "100%",
              borderRadius: { xs: 3, sm: 4 },
              backgroundColor: "rgba(255, 255, 255, 0.98)",
              backdropFilter: "blur(10px)",
              boxShadow: "0 8px 32px rgba(0, 0, 0, 0.2)",
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: { xs: 1.5, sm: 2 },
                marginBottom: { xs: 2, sm: 3 },
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: { xs: 50, sm: 60, md: 70 },
                  height: { xs: 50, sm: 60, md: 70 },
                  borderRadius: "12px",
                  backgroundColor: "rgba(24, 116, 210, 0.1)",
                  padding: { xs: 1, sm: 1.5 },
                  boxShadow: "0 4px 12px rgba(24, 116, 210, 0.15)",
                }}
              >
                <img
                  src={PMCLogo}
                  alt="Emblem of India"
                  style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "contain",
                  }}
                />
              </Box>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: { xs: 50, sm: 60, md: 70 },
                  height: { xs: 50, sm: 60, md: 70 },
                  borderRadius: "12px",
                  backgroundColor: "rgba(24, 116, 210, 0.1)",
                  padding: { xs: 1, sm: 1.5 },
                  boxShadow: "0 4px 12px rgba(24, 116, 210, 0.15)",
                }}
              >
                <img
                  src={PMC}
                  alt="Seal of Maharashtra"
                  style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "contain",
                  }}
                />
              </Box>
            </Box>

            <Typography
              component="h1"
              variant="h4"
              sx={{
                fontWeight: 700,
                marginBottom: { xs: 0.5, sm: 1 },
                fontSize: { xs: "1.75rem", sm: "2rem", md: "2.25rem" },
                color: "#061c36",
                textAlign: "center",
              }}
            >
              Sign In
            </Typography>

            <Typography
              variant="body1"
              sx={{
                marginBottom: { xs: 2.5, sm: 3, md: 4 },
                color: "#666",
                fontSize: { xs: "0.875rem", sm: "1rem" },
                textAlign: "center",
                fontWeight: 500,
              }}
            >
              General Administration Department Portal
            </Typography>

            <Box
              component="form"
              onSubmit={handleSubmit}
              sx={{
                width: "100%",
                display: "flex",
                flexDirection: "column",
                gap: { xs: 1.5, sm: 2 },
              }}
            >
              <TextField
                required
                fullWidth
                id="username"
                label="Username"
                name="username"
                autoComplete="username"
                autoFocus
                value={formData.username}
                onChange={handleInputChange}
                variant="outlined"
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "12px",
                    fontSize: { xs: "0.95rem", sm: "1rem" },
                    "& fieldset": {
                      borderColor: "rgba(24, 116, 210, 0.2)",
                    },
                    "&:hover fieldset": {
                      borderColor: "rgba(24, 116, 210, 0.4)",
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: "rgba(24, 116, 210, 1)",
                      borderWidth: "2px",
                    },
                  },
                  "& .MuiInputLabel-root": {
                    fontSize: { xs: "0.95rem", sm: "1rem" },
                  },
                }}
              />

              <TextField
                required
                fullWidth
                name="password"
                label="Password"
                type={showPassword ? "text" : "password"}
                id="password"
                autoComplete="current-password"
                value={formData.password}
                onChange={handleInputChange}
                variant="outlined"
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "12px",
                    fontSize: { xs: "0.95rem", sm: "1rem" },
                    "& fieldset": {
                      borderColor: "rgba(24, 116, 210, 0.2)",
                    },
                    "&:hover fieldset": {
                      borderColor: "rgba(24, 116, 210, 0.4)",
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: "rgba(24, 116, 210, 1)",
                      borderWidth: "2px",
                    },
                  },
                  "& .MuiInputLabel-root": {
                    fontSize: { xs: "0.95rem", sm: "1rem" },
                  },
                }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={handleTogglePasswordVisibility}
                        edge="end"
                        sx={{
                          color: "rgba(24, 116, 210, 0.6)",
                          "&:hover": {
                            color: "rgba(24, 116, 210, 1)",
                            backgroundColor: "rgba(24, 116, 210, 0.08)",
                          },
                        }}
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              {error && (
                <Alert
                  severity="error"
                  sx={{
                    marginTop: { xs: 0.5, sm: 1 },
                    borderRadius: "12px",
                    fontSize: { xs: "0.875rem", sm: "0.9rem" },
                    "& .MuiAlert-icon": {
                      fontSize: { xs: "20px", sm: "22px" },
                    },
                  }}
                >
                  {error}
                </Alert>
              )}

              <Button
                type="submit"
                fullWidth
                variant="contained"
                sx={{
                  marginTop: { xs: 1, sm: 2 },
                  marginBottom: { xs: 1, sm: 2 },
                  py: { xs: 1.25, sm: 1.5, md: 1.75 },
                  borderRadius: "12px",
                  fontSize: { xs: "0.95rem", sm: "1rem", md: "1.05rem" },
                  fontWeight: 600,
                  backgroundColor: "rgba(24, 116, 210, 1)",
                  textTransform: "none",
                  boxShadow: "0 4px 12px rgba(24, 116, 210, 0.3)",
                  "&:hover": {
                    backgroundColor: "rgba(24, 116, 210, 0.9)",
                    boxShadow: "0 6px 20px rgba(24, 116, 210, 0.4)",
                    transform: "translateY(-1px)",
                  },
                  "&:disabled": {
                    backgroundColor: "rgba(24, 116, 210, 0.5)",
                    color: "rgba(255, 255, 255, 0.7)",
                  },
                  transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                }}
                disabled={isLoading}
              >
                {isLoading ? (
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                    }}
                  >
                    <CircularProgress
                      size={20}
                      sx={{ color: "rgba(255, 255, 255, 0.9)" }}
                    />
                    <Typography
                      sx={{
                        fontSize: { xs: "0.9rem", sm: "1rem" },
                        fontWeight: 600,
                      }}
                    >
                      Signing in...
                    </Typography>
                  </Box>
                ) : (
                  "Sign In"
                )}
              </Button>
            </Box>
          </Paper>
        </Box>
      </Container>
    </Box>
  );
};

export default LoginPage;
